import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { skills } from '../data/portfolio'; 

export default function Skills() {
  const [ref, inView] = useInView(0.1);
  const [active, setActive] = useState(0);

  return (
    <section id="skills" className="section" style={{ 
      background: '#030508', 
      position: 'relative', 
      overflow: 'hidden',
      transition: 'background 0.8s ease' // Smooth transition if you decide to tint the whole bg later
    }}>
      
      {/* DYNAMIC BACKGROUND GLOW */}
      {/* This creates a large, soft glow that changes color based on the active tab */}
      <motion.div
        animate={{
          background: `radial-gradient(800px circle at 50% 40%, ${skills[active].color}08, transparent)`
        }}
        transition={{ duration: 1 }}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header style={{ marginBottom: '4rem' }}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              animate={{ color: skills[active].color }}
              style={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3em', transition: 'color 0.4s' }}
            >
              03. Capabilities
            </motion.span>
            <h2 style={{ fontSize: 'clamp(2.5rem, 6vw, 3.5rem)', color: '#fff', marginTop: '0.5rem', fontWeight: 800 }}>
              Technical{' '}
              <motion.span 
                animate={{ color: skills[active].color }} 
                transition={{ duration: 0.5 }}
              >
                Stack.
              </motion.span>
            </h2>
          </motion.div>
        </header>

        {/* Navigation Tabs */}
        <nav style={{ 
          display: 'inline-flex', background: 'rgba(255,255,255,0.03)', 
          padding: '6px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)',
          marginBottom: '3rem', flexWrap: 'wrap',
          backdropFilter: 'blur(10px)'
        }}>
          {skills.map((cat, i) => (
            <button
              key={cat.category}
              onClick={() => setActive(i)}
              style={{
                position: 'relative', border: 'none', background: 'none', padding: '0.75rem 1.5rem',
                cursor: 'pointer', color: active === i ? '#fff' : '#94a3b8',
                fontFamily: 'inherit', fontWeight: 600, fontSize: '0.8rem', transition: 'color 0.3s'
              }}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>{cat.category}</span>
              {active === i && (
                <motion.div
                  layoutId="active-pill"
                  style={{
                    position: 'absolute', inset: 0, 
                    background: `${cat.color}15`,
                    borderRadius: '10px', 
                    border: `1px solid ${cat.color}30`, 
                    zIndex: 1,
                    boxShadow: `0 0 20px ${cat.color}10`
                  }}
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                />
              )}
            </button>
          ))}
        </nav>

        {/* Skills Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '1rem',
              marginBottom: '5rem'
            }}
          >
            {skills[active].items.map((skill) => (
              <motion.div
                key={skill}
                whileHover={{ y: -5, background: 'rgba(255,255,255,0.04)' }}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontSize: '0.95rem', color: '#fff', fontWeight: 500 }}>{skill}</div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* --- METHODOLOGY SECTION --- */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            padding: '3rem', borderRadius: '24px',
            background: 'linear-gradient(145deg, rgba(255,255,255,0.03), transparent)',
            border: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '3rem', flexWrap: 'wrap',
            backdropFilter: 'blur(5px)'
          }}
        >
          <div style={{ flex: '1 1 300px' }}>
            <h3 style={{ color: '#fff', fontSize: '1.5rem', marginBottom: '1rem' }}>The Methodology</h3>
            <p style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.95rem' }}>
              I focus on scalable architecture and clean code. My approach involves deeply understanding the 
              underlying protocols before jumping into high-level abstractions.
            </p>
          </div>
          <div style={{ flex: '2 1 400px', display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            {skills.map(cat => (
              <div key={cat.category} style={{ flex: '1 1 150px' }}>
                <h4 style={{ 
                  color: cat.color, 
                  fontSize: '0.65rem', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.2em', 
                  marginBottom: '0.5rem', 
                  transition: 'color 0.6s ease' 
                }}>
                  {cat.category}
                </h4>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.6 }}>
                  {cat.items.join(' • ')}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}